import { useEffect, useState, useCallback, useMemo } from "react";
import { useOutletContext } from "react-router-dom";
import {
  CalendarClock, Search, ChevronLeft, ChevronRight, RotateCcw, Video, Loader2, Plus,
  MoreVertical, CheckCircle2, XCircle, X,
} from "lucide-react";
import api, { apiError } from "@/lib/api";
import { ErrorView, EmptyState } from "@/components/StateViews";
import { ActivityStatusChip } from "@/components/Chips";
import LeadDrawer from "@/components/LeadDrawer";
import ActivityModal from "@/components/ActivityModal";
import MeetingActionModal from "@/components/MeetingActionModal";
import DateRangePicker from "@/components/DateRangePicker";
import { Checkbox } from "@/components/ui/checkbox";
import {
  DropdownMenu, DropdownMenuTrigger, DropdownMenuContent, DropdownMenuItem,
} from "@/components/ui/dropdown-menu";
import { useAuth } from "@/contexts/AuthContext";
import { fmtDateTime, fmtDate } from "@/lib/format";
import PageSizeSelector from "@/components/PageSizeSelector";

const dash = (v) => (v === 0 || v ? v : "-");
const ACTIVE_STATUSES = ["Pending", "Snoozed"];

export default function Meetings() {
  const { users } = useOutletContext();
  const { user, options } = useAuth();
  const isManager = user?.role !== "Sales Executive";

  const [filters, setFilters] = useState({ fromDate: "", toDate: "", salespersonId: "", status: "" });
  const [searchInput, setSearchInput] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const [sortOrder, setSortOrder] = useState("desc");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selected, setSelected] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [picked, setPicked] = useState([]); // selected meeting ids for bulk
  const [rowAction, setRowAction] = useState(null); // { action, meeting }
  const [bulkAction, setBulkAction] = useState(null); // action string

  // debounce the search box
  useEffect(() => {
    const t = setTimeout(() => { setSearch(searchInput.trim()); setPage(1); }, 400);
    return () => clearTimeout(t);
  }, [searchInput]);

  const statuses = data?.statuses || options?.activity_statuses || [];
  const outcomes = data?.outcomes || [];

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const params = { page, limit: pageSize, sortBy: "due_at", sortOrder };
      if (filters.fromDate) params.fromDate = filters.fromDate;
      if (filters.toDate) params.toDate = filters.toDate;
      if (filters.status) params.status = filters.status;
      if (isManager && filters.salespersonId) params.userId = filters.salespersonId;
      if (search) params.search = search;
      const { data } = await api.get("/activities/meetings", { params });
      setData(data.data);
    } catch (e) { setError(apiError(e)); }
    finally { setLoading(false); }
  }, [page, pageSize, sortOrder, filters, search, isManager]);

  useEffect(() => { load(); }, [load]);
  // clear selection whenever the result set changes
  useEffect(() => { setPicked([]); }, [data]);

  const canEdit = useCallback((m) => isManager
    || m.assigned_to_user_id === user?.id
    || m.lead_owner_user_id === user?.id, [isManager, user]);
  const isActive = (m) => ACTIVE_STATUSES.includes(m.status);

  const afterAction = () => { setPicked([]); load(); };

  const setFilter = (k, v) => { setFilters((f) => ({ ...f, [k]: v })); setPage(1); };
  const onRangeChange = ({ created_from, created_to }) => {
    setFilters((f) => ({ ...f, fromDate: created_from, toDate: created_to }));
    setPage(1);
  };
  const clearFilters = () => {
    setFilters({ fromDate: "", toDate: "", salespersonId: "", status: "" });
    setSearchInput(""); setSearch(""); setPage(1);
  };

  const activeFilters = useMemo(
    () => [filters.fromDate, filters.toDate, filters.salespersonId, filters.status, search].filter(Boolean).length,
    [filters, search]);

  const items = data?.items || [];
  const total = data?.total || 0;
  const pages = data?.pages || 1;

  // rows the current user may bulk-action on (any editable meeting, regardless of status)
  const eligibleIds = useMemo(() => items.filter((m) => canEdit(m)).map((m) => m.id), [items, canEdit]);
  const toggleRow = (id) => setPicked((p) => (p.includes(id) ? p.filter((x) => x !== id) : [...p, id]));
  const toggleAll = () => setPicked((p) => (p.length === eligibleIds.length ? [] : [...eligibleIds]));
  const allSelected = eligibleIds.length > 0 && picked.length === eligibleIds.length;

  const columns = ["Date & Time", "Lead", "Phone", "Email", "Brand / Company", "Services",
    "Salesperson", "Internal Team", "Status", "Outcome", "Next Follow-up", "Created By", "Last Updated"];

  return (
    <div className="space-y-6" data-testid="meetings-page">
      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="flex items-center gap-2 font-sora text-3xl font-bold text-navy"><Video className="h-7 w-7 text-loop" />Meetings</h1>
          <p className="text-sm text-ink/60">{data ? `${total} meeting${total === 1 ? "" : "s"} match your filters.` : "Loading…"}</p>
        </div>
        <button onClick={() => setModalOpen(true)} data-testid="add-meeting-button" className="flex items-center gap-2 rounded-xl bg-loop px-4 py-2.5 text-sm font-semibold text-white hover:bg-loophover">
          <Plus className="h-4 w-4" />Schedule Meeting
        </button>
      </div>

      {/* Filters */}
      <div className="space-y-3 rounded-2xl border border-sky/70 bg-white p-4" data-testid="meetings-filter-panel">
        <div className="flex flex-wrap items-center gap-2">
          {isManager && (
            <select data-testid="meetings-salesperson" value={filters.salespersonId} onChange={(e) => setFilter("salespersonId", e.target.value)} className="h-9 rounded-lg border border-sky bg-white px-2 text-sm text-navy outline-none focus:border-loop" title="Team Member (assignee, creator, or participant)">
              <option value="">All team members</option>
              {(users || []).map((u) => <option key={u.id} value={u.id}>{u.name}</option>)}
            </select>
          )}
          <select data-testid="meetings-status" value={filters.status} onChange={(e) => setFilter("status", e.target.value)} className="h-9 rounded-lg border border-sky bg-white px-2 text-sm text-navy outline-none focus:border-loop">
            <option value="">All statuses</option>
            {statuses.map((s) => <option key={s} value={s}>{s}</option>)}
          </select>
          <div className="relative">
            <Search className="absolute left-2.5 top-2.5 h-4 w-4 text-ink/40" />
            <input data-testid="meetings-search" value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Search name, phone, email, brand…" className="h-9 w-64 rounded-lg border border-sky bg-white pl-8 pr-2 text-sm text-navy outline-none focus:border-loop" />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <DateRangePicker from={filters.fromDate} to={filters.toDate} onChange={onRangeChange} testid="meetings-range" future />
            <button data-testid="meetings-clear" onClick={clearFilters} disabled={!activeFilters} className="flex items-center gap-1.5 rounded-lg border border-sky px-3 py-2 text-sm font-medium text-ink/60 hover:bg-mist disabled:opacity-40"><RotateCcw className="h-3.5 w-3.5" />Clear filters</button>
          </div>
        </div>
      </div>

      {error ? (
        <ErrorView message="Couldn't load meetings. Please try again." onRetry={load} />
      ) : loading ? (
        <div className="flex items-center justify-center rounded-2xl border border-sky/70 bg-white py-20" data-testid="meetings-loading">
          <Loader2 className="h-6 w-6 animate-spin text-loop" />
        </div>
      ) : items.length === 0 ? (
        <EmptyState icon={CalendarClock} title="No meetings found for the selected filters." subtitle="Try clearing filters or schedule one with the Add Meeting button." />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-sky/70 bg-white">
          {/* Bulk action toolbar */}
          {picked.length > 0 && (
            <div className="flex flex-wrap items-center gap-3 border-b border-sky/60 bg-sky/15 px-4 py-2.5" data-testid="meetings-bulk-toolbar">
              <span className="text-sm font-semibold text-navy">{picked.length} selected</span>
              <div className="flex flex-wrap items-center gap-2">
                <button onClick={() => setBulkAction("completed")} data-testid="bulk-mark-done" className="flex items-center gap-1.5 rounded-lg bg-emerald-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700"><CheckCircle2 className="h-3.5 w-3.5" />Mark as Done</button>
                <button onClick={() => setBulkAction("rescheduled")} data-testid="bulk-reschedule" className="flex items-center gap-1.5 rounded-lg bg-loop px-3 py-1.5 text-xs font-semibold text-white hover:bg-loophover"><CalendarClock className="h-3.5 w-3.5" />Reschedule</button>
                <button onClick={() => setBulkAction("cancelled")} data-testid="bulk-cancel" className="flex items-center gap-1.5 rounded-lg bg-red-600 px-3 py-1.5 text-xs font-semibold text-white hover:bg-red-700"><XCircle className="h-3.5 w-3.5" />Cancel</button>
              </div>
              <button onClick={() => setPicked([])} data-testid="bulk-clear" className="ml-auto flex items-center gap-1 text-xs font-medium text-ink/55 hover:text-navy"><X className="h-3.5 w-3.5" />Clear selection</button>
            </div>
          )}
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="meetings-table">
              <thead className="bg-mist/60 text-left text-xs text-ink/55">
                <tr>
                  <th className="w-10 px-3 py-3">
                    <Checkbox checked={allSelected} onCheckedChange={toggleAll} disabled={!eligibleIds.length} data-testid="meetings-select-all" aria-label="Select all eligible meetings" />
                  </th>
                  <th className="cursor-pointer whitespace-nowrap px-4 py-3 font-semibold" data-testid="meetings-sort-date" onClick={() => setSortOrder((o) => (o === "desc" ? "asc" : "desc"))}>
                    {columns[0]} {sortOrder === "desc" ? "↓" : "↑"}
                  </th>
                  {columns.slice(1).map((c) => <th key={c} className="whitespace-nowrap px-4 py-3 font-semibold">{c}</th>)}
                  <th className="whitespace-nowrap px-4 py-3 font-semibold text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-sky/40">
                {items.map((m) => {
                  const editable = canEdit(m);
                  const active = isActive(m);
                  return (
                  <tr key={m.id} data-testid={`meeting-row-${m.id}`} className="transition-colors hover:bg-sky/20">
                    <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}>
                      {editable ? (
                        <Checkbox checked={picked.includes(m.id)} onCheckedChange={() => toggleRow(m.id)} data-testid={`meeting-select-${m.id}`} aria-label="Select meeting" />
                      ) : <span className="block h-4 w-4" />}
                    </td>
                    <td className="cursor-pointer whitespace-nowrap px-4 py-3 text-navy" onClick={() => setSelected(m.lead_id)}>{m.due_at ? fmtDateTime(m.due_at) : "-"}</td>
                    <td className="cursor-pointer whitespace-nowrap px-4 py-3 font-medium text-navy" onClick={() => setSelected(m.lead_id)}>{dash(m.lead_name)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink/70">{dash(m.phone)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink/70">{dash(m.email)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink/70">{dash(m.business_name)}</td>
                    <td className="max-w-[180px] truncate px-4 py-3 text-ink/70" title={m.service_interested || ""}>{dash(m.service_interested)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink/70">{dash(m.assigned_to_name)}</td>
                    <td className="max-w-[200px] px-4 py-3 text-ink/70">
                      {m.internal_team_members && m.internal_team_members.length > 0 ? (
                        <span className="flex flex-wrap gap-1">
                          {m.internal_team_members.map((tm) => (
                            <span key={tm.id} className="rounded-full bg-sky/50 px-2 py-0.5 text-[10px] font-semibold text-navy">{tm.name}</span>
                          ))}
                        </span>
                      ) : "-"}
                    </td>
                    <td className="whitespace-nowrap px-4 py-3">{m.status ? <ActivityStatusChip value={m.status} /> : "-"}</td>
                    <td className="max-w-[160px] truncate px-4 py-3 text-ink/60" title={m.meeting_outcome || ""}>{m.meeting_outcome ? <span className="rounded-full bg-emerald-50 px-2 py-0.5 text-[10px] font-semibold text-emerald-700">{m.meeting_outcome}</span> : "-"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink/70">{m.next_follow_up_at ? fmtDate(m.next_follow_up_at) : "-"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink/70">{dash(m.created_by_name)}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-ink/55">{m.updated_at ? fmtDateTime(m.updated_at) : "-"}</td>
                    <td className="whitespace-nowrap px-4 py-3 text-right" onClick={(e) => e.stopPropagation()}>
                      {editable && active ? (
                        <div className="flex items-center justify-end gap-1.5">
                          <button onClick={() => setRowAction({ action: "completed", meeting: m })} data-testid={`meeting-done-${m.id}`} className="rounded-lg bg-emerald-600 px-2.5 py-1.5 text-xs font-semibold text-white hover:bg-emerald-700">Done</button>
                          <DropdownMenu>
                            <DropdownMenuTrigger asChild>
                              <button data-testid={`meeting-more-${m.id}`} className="rounded-lg border border-sky p-1.5 text-ink/60 hover:bg-mist"><MoreVertical className="h-4 w-4" /></button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent align="end">
                              <DropdownMenuItem data-testid={`meeting-reschedule-${m.id}`} onClick={() => setRowAction({ action: "rescheduled", meeting: m })}><CalendarClock className="mr-2 h-4 w-4" />Reschedule</DropdownMenuItem>
                              <DropdownMenuItem data-testid={`meeting-cancel-${m.id}`} onClick={() => setRowAction({ action: "cancelled", meeting: m })} className="text-red-600 focus:text-red-600"><XCircle className="mr-2 h-4 w-4" />Cancel</DropdownMenuItem>
                            </DropdownMenuContent>
                          </DropdownMenu>
                        </div>
                      ) : <span className="text-xs text-ink/30">—</span>}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-sky/50 px-4 py-3 text-sm">
            <p className="text-ink/55" data-testid="meetings-page-info">Page {page} of {pages} · {total} total</p>
            <div className="flex items-center gap-3">
              <PageSizeSelector value={pageSize} onChange={(n) => { setPageSize(n); setPage(1); }} testid="meetings-page-size" />
              <div className="flex items-center gap-2">
                <button data-testid="meetings-prev" disabled={page <= 1} onClick={() => setPage((p) => Math.max(1, p - 1))} className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 font-medium text-navy hover:bg-mist disabled:opacity-40"><ChevronLeft className="h-4 w-4" />Prev</button>
                <button data-testid="meetings-next" disabled={page >= pages} onClick={() => setPage((p) => Math.min(pages, p + 1))} className="flex items-center gap-1 rounded-lg border border-sky px-3 py-1.5 font-medium text-navy hover:bg-mist disabled:opacity-40">Next<ChevronRight className="h-4 w-4" /></button>
              </div>
            </div>
          </div>
        </div>
      )}

      <LeadDrawer leadId={selected} open={!!selected} onOpenChange={(v) => !v && setSelected(null)} onUpdated={load} users={users} />
      <ActivityModal open={modalOpen} onOpenChange={setModalOpen} mode="meeting" users={users} onSaved={() => { setModalOpen(false); load(); }} />

      {rowAction && (
        <MeetingActionModal
          open={!!rowAction}
          onOpenChange={(v) => !v && setRowAction(null)}
          action={rowAction.action}
          mode="single"
          meeting={rowAction.meeting}
          outcomes={outcomes}
          onDone={afterAction}
        />
      )}
      {bulkAction && (
        <MeetingActionModal
          open={!!bulkAction}
          onOpenChange={(v) => !v && setBulkAction(null)}
          action={bulkAction}
          mode="bulk"
          ids={picked}
          outcomes={outcomes}
          onDone={afterAction}
        />
      )}
    </div>
  );
}
